import { useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  getAdditionalUserInfo,
} from 'firebase/auth';
import {
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
  getDocFromServer,
} from 'firebase/firestore';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { httpsCallable } from 'firebase/functions'; // <--- Added import
import { auth, db, functionsInstance } from '../firebase/firebase';

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userAccessData, setUserAccessData] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);

  // State for Auth Modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMessage, setAuthModalMessage] = useState('');
  const [authMode, setAuthMode] = useState('signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [tptStoreUrlInput, setTptStoreUrlInput] = useState('');

  // --- NEW HELPER: Track IP on Signup ---
  const trackSignup = async (userEmail) => {
    try {
      const logIpFn = httpsCallable(functionsInstance, 'logIpSignup');
      await logIpFn({ email: userEmail });
      console.log("Signup IP tracking logged.");
    } catch (e) {
      console.error("IP Logging failed:", e);
      // If the backend threw "permission-denied", it means they are blocked.
      if (e.code === 'permission-denied' || e.message?.includes('blocked')) {
        await signOut(auth);
        setAuthError("Registration failed: This network has been blocked due to suspicious activity.");
        throw new Error("Network Blocked"); // Stop execution flow
      }
    }
  };

  const attributeReferralAfterLogin = async () => {
    try {
      const user = auth.currentUser;
      if (!user) return;
      const idToken = await user.getIdToken();

      await fetch('/ref/assign', {
        method: 'POST',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      console.log('Referral attribution check completed.');
    } catch (error) {
      console.error('Referral attribution check failed:', error);
    }
  };

  const ensureUserDocOnSignup = async (user) => {
    try {
      const ref = doc(db, 'users', user.uid);
      const snap = await getDocFromServer(ref).catch(() => null);
      if (snap && snap.exists()) return;

      await setDoc(
        ref,
        {
          email: user.email ?? null,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
      console.log('ensureUserDocOnSignup: created minimal user doc.');
    } catch (e) {
      console.warn('ensureUserDocOnSignup failed (safe to ignore):', e);
    }
  };

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        attributeReferralAfterLogin();
        const userDocRef = doc(db, 'users', user.uid);
        const unsubscribeFirestore = onSnapshot(
          userDocRef,
          (docSnap) => {
            if (docSnap.exists()) {
              setUserAccessData(docSnap.data());
            } else {
              setUserAccessData(null);
            }
            setIsAuthLoading(false);
          },
          (firestoreError) => {
            console.error('Error fetching user access data:', firestoreError);
            setUserAccessData(null);
            setIsAuthLoading(false);
          }
        );
        return () => unsubscribeFirestore();
      } else {
        setCurrentUser(null);
        setUserAccessData(null);
        setIsAuthLoading(false);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const handleAuthAction = async (actionType) => {
    if (!auth) {
      setAuthError('Authentication service is not ready. Please try again in a moment.');
      return;
    }
    setAuthError('');
    setResetMessage('');
    if (!email || !password) {
      setAuthError('Email and password are required.');
      return;
    }
    if (actionType === 'signup' && !email.includes('@')) {
      setAuthError('Please enter a valid email address.');
      return;
    }

    try {
      const analytics = getAnalytics();
      if (actionType === 'signup') {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // 1. Create Doc
        await ensureUserDocOnSignup(user);

        // 2. Track IP (Will throw if blocked)
        await trackSignup(user.email);

        if (analytics && user) {
          const adSource = sessionStorage.getItem('ad_source');
          logEvent(analytics, 'sign_up', {
            method: 'email_password',
            source: adSource || 'organic',
          });

          if (typeof window.gtag === 'function') {
            window.gtag('event', 'conversion', {
              send_to: 'AW-17181144542/5imnCOronNcaEN7rzYBA',
            });
            console.log('Google Ads conversion event fired for email sign-up.');
          }
        }
        setShowAuthModal(false);
        setTptStoreUrlInput('');
      } else {
        await signInWithEmailAndPassword(auth, email, password);
        const analytics = getAnalytics();
        if (analytics) {
          logEvent(analytics, 'login', { method: 'email_password' });
        }
        setShowAuthModal(false);
      }
    } catch (authCatchError) {
      // Don't override if trackSignup already set a block error
      if (authCatchError.message === "Network Blocked") return;

      let friendlyMessage = authCatchError.message.replace('Firebase: ', '');
      if (authCatchError.code === 'auth/email-already-in-use') {
        friendlyMessage = 'This email address is already in use. Please try logging in or use a different email.';
      } else if (
        authCatchError.code === 'auth/wrong-password' ||
        authCatchError.code === 'auth/user-not-found' ||
        authCatchError.code === 'auth/invalid-credential'
      ) {
        friendlyMessage = 'Invalid email or password. Please check your credentials and try again.';
      }
      setAuthError(friendlyMessage);
      console.error(`Error during ${actionType}:`, authCatchError);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    if (!auth) {
      setAuthError('Authentication service is not ready. Please try again in a moment.');
      return;
    }
    setAuthError('');
    setResetMessage('');
    try {
      const analytics = getAnalytics();
      const result = await signInWithPopup(auth, provider);

      const additionalUserInfo = getAdditionalUserInfo(result);
      const adSource = sessionStorage.getItem('ad_source');

      if (additionalUserInfo?.isNewUser) {
        // 1. Create Doc
        await ensureUserDocOnSignup(result.user);

        // 2. Track IP (Will throw if blocked)
        await trackSignup(result.user.email);

        logEvent(analytics, 'sign_up', {
          method: 'google',
          source: adSource || 'organic',
        });

        if (typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', {
            send_to: 'AW-17181144542/5imnCOronNcaEN7rzYBA',
          });
          console.log('Google Ads conversion event fired for Google sign-up.');
        }
      } else {
        logEvent(analytics, 'login', { method: 'google' });
      }

      setShowAuthModal(false);
    } catch (error) {
       // Don't override if trackSignup already set a block error
      if (error.message === "Network Blocked") return;

      let friendlyMessage = error.message.replace('Firebase: ', '');
      if (error.code === 'auth/popup-closed-by-user') {
        friendlyMessage = 'Sign-in window closed before completion. Please try again.';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        friendlyMessage =
          'An account already exists with this email address. Please sign in using the original method.';
      }
      setAuthError(friendlyMessage);
      console.error('Google Sign-In Error:', error);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!auth) {
      setAuthError('Authentication service is not ready. Please try again.');
      return;
    }
    setAuthError('');
    setResetMessage('');
    if (!resetEmail) {
      setAuthError('Please enter your email address to reset the password.');
      return;
    }
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Please check your inbox (and spam folder).');
      const analytics = getAnalytics();
      if (analytics) {
        logEvent(analytics, 'send_password_reset_email');
      }
    } catch (passwordResetError) {
      let friendlyMessage = passwordResetError.message.replace('Firebase: ', '');
      if (passwordResetError.code === 'auth/user-not-found' || passwordResetError.code === 'auth/invalid-email') {
        friendlyMessage = 'No user found with this email address, or the email is invalid.';
      }
      setAuthError(friendlyMessage);
      console.error('Error sending password reset email:', passwordResetError);
    }
  };

  const handleLogout = async () => {
    if (!auth) {
      console.error('Firebase auth not initialized for logout.');
      return;
    }
    try {
      const analytics = getAnalytics();
      if (analytics) {
        logEvent(analytics, 'logout');
      }
      await signOut(auth);
    } catch (logoutError) {
      console.error('Error signing out:', logoutError);
    }
  };

  const triggerAuthModal = (message = 'Please log in or sign up to continue.', mode = 'signup') => {
    setAuthModalMessage(message);
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  return {
    currentUser,
    userAccessData,
    isAuthLoading,
    handleLogout,

    // Auth Modal State and Handlers
    showAuthModal,
    setShowAuthModal,
    authModalMessage,
    setAuthModalMessage,
    authMode,
    setAuthMode,
    email,
    setEmail,
    password,
    setPassword,
    authError,
    setAuthError,
    showForgotPassword,
    setShowForgotPassword,
    resetEmail,
    setResetEmail,
    resetMessage,
    setResetMessage,
    tptStoreUrlInput,
    setTptStoreUrlInput,
    handleAuthAction,
    handlePasswordReset,
    triggerAuthModal,
    handleGoogleSignIn,
  };
};